import { RefreshCw } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Progress } from '@/components/ui/progress';
import { Separator } from '@/components/ui/separator';

// Mock data
const mockResources = {
  servers: [
    {
      id: 'droplet-1',
      name: 'n8n-production',
      cpu: 45,
      memory: 68,
      disk: 32,
      status: 'healthy',
      uptime: '15d 4h'
    },
    {
      id: 'droplet-2',
      name: 'vapi-worker',
      cpu: 23,
      memory: 41,
      disk: 28,
      status: 'healthy',
      uptime: '12d 18h'
    }
  ],
  db: {
    connections: 45,
    maxConnections: 100,
    storageUsed: 2.4,
    storageTotal: 10,
    avgQueryTime: 23,
    activeQueries: 12,
    cacheHitRate: 94.5
  }
};

interface ResourceGaugeProps {
  label: string;
  current?: number;
  max?: number;
  value?: number;
  unit: string;
  showPercentage?: boolean;
  status?: 'success' | 'warning' | 'error';
}

function ResourceGauge({ 
  label, 
  current, 
  max, 
  value,
  unit,
  showPercentage = false,
  status
}: ResourceGaugeProps) {
  const percentage = max && current ? (current / max) * 100 : 0;
  const displayValue = value || current;
  
  const getStatus = () => {
    if (status) return status;
    if (percentage >= 90) return 'error';
    if (percentage >= 70) return 'warning';
    return 'success';
  };

  const statusColors = {
    success: '#10b981',
    warning: '#f59e0b',
    error: '#ef4444'
  };

  const currentStatus = getStatus();

  return (
    <div className="space-y-2">
      <div className="flex justify-between text-sm">
        <span>{label}</span>
        <span className="font-mono">
          {displayValue}{unit && ` ${unit}`}
          {max && ` / ${max}${unit && ` ${unit}`}`}
          {showPercentage && ` (${percentage.toFixed(1)}%)`}
        </span>
      </div>
      {max && current && (
        <Progress 
          value={percentage} 
          className="h-2"
          style={{ 
            background: `linear-gradient(to right, ${statusColors[currentStatus]} 0%, ${statusColors[currentStatus]} ${percentage}%, #e5e7eb ${percentage}%, #e5e7eb 100%)`
          }}
        />
      )}
    </div>
  );
}

interface ServerResourceCardProps {
  server: any;
  showDetails?: boolean;
  onRestart?: () => void;
}

function ServerResourceCard({ server, showDetails, onRestart }: ServerResourceCardProps) {
  const getStatusColor = (status: string) => {
    switch (status) {
      case 'healthy': return 'bg-green-500';
      case 'warning': return 'bg-yellow-500';
      case 'error': return 'bg-red-500';
      default: return 'bg-gray-500';
    }
  };

  return (
    <Card>
      <CardHeader className="pb-3">
        <div className="flex items-center justify-between">
          <CardTitle className="text-sm">{server.name}</CardTitle>
          <div className="flex items-center gap-2">
            <div className={`w-2 h-2 rounded-full ${getStatusColor(server.status)}`} />
            {onRestart && (
              <Button size="sm" variant="ghost" onClick={onRestart}>
                <RefreshCw className="w-3 h-3" />
              </Button>
            )}
          </div>
        </div>
      </CardHeader>
      <CardContent>
        <div className="space-y-3">
          <ResourceGauge
            label="CPU"
            current={server.cpu}
            max={100}
            unit="%"
          />
          <ResourceGauge
            label="Memory"
            current={server.memory}
            max={100}
            unit="%"
          />
          <ResourceGauge
            label="Disk"
            current={server.disk}
            max={100}
            unit="%"
          />
          {showDetails && (
            <>
              <Separator />
              <div className="text-xs text-muted-foreground">
                Uptime: {server.uptime}
              </div>
            </>
          )}
        </div>
      </CardContent>
    </Card>
  );
}

const getQueryTimeStatus = (time: number) => {
  if (time < 50) return 'success';
  if (time < 100) return 'warning';
  return 'error';
};

export function InfrastructureStatus() {
  const resources = mockResources;

  const restartServer = (serverId: string) => {
    console.log('Restarting server:', serverId);
  };

  return (
    <div className="grid grid-cols-3 gap-4">
      {/* Digital Ocean Droplets */}
      <Card className="col-span-2">
        <CardHeader>
          <CardTitle className="text-base">Server Resources</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            {resources.servers.map((server) => (
              <ServerResourceCard
                key={server.id}
                server={server}
                showDetails
                onRestart={() => restartServer(server.id)}
              />
            ))}
          </div>
        </CardContent>
      </Card>

      {/* Database Metrics */}
      <Card>
        <CardHeader>
          <CardTitle className="text-base">Database</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-3">
            <ResourceGauge
              label="Connections"
              current={resources.db.connections}
              max={resources.db.maxConnections}
              unit="active"
            />
            <ResourceGauge
              label="Storage"
              current={resources.db.storageUsed}
              max={resources.db.storageTotal}
              unit="GB"
              showPercentage
            />
            <ResourceGauge
              label="Query Time"
              value={resources.db.avgQueryTime}
              unit="ms"
              status={getQueryTimeStatus(resources.db.avgQueryTime)}
            />
            
            <Separator />
            
            <div className="space-y-2">
              <div className="flex justify-between text-xs">
                <span>Active Queries</span>
                <span className="font-mono">{resources.db.activeQueries}</span>
              </div>
              <div className="flex justify-between text-xs">
                <span>Cache Hit Rate</span>
                <span className="font-mono">{resources.db.cacheHitRate}%</span>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}